// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';
import { dispatch } from '../index';
import * as R from 'ramda';

// types
import { DefaultRootStateProps } from 'types';
import { Execution } from 'types/stateMachines';

// ----------------------------------------------------------------------

const initialState: DefaultRootStateProps['stateMachines'] = {
    error: null,
    stateMachinesData: [],
    executionsData: [],
    executionHistoryData: [],
    executionDetail: null,
    definition: null,
    loading: false,
    nextToken: null
};

type GetProps = {
    stateMachineId: string | undefined;
    search?: string | null;
    filter?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    nextToken?: string | null;
    reset?: boolean;
};

const slice = createSlice({
    name: 'stateMachines',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },
        getStateMachinesListSuccess(state, action) {
            state.stateMachinesData = Array.isArray(action.payload) ? action.payload : action.payload?.stateMachines || [];
        },
        getExecutionsListSuccess(state, action) {
            const currentNextToken = action?.payload?.headers?.['x-next-page-token'];
            state.nextToken = currentNextToken || null;

            const isReset = action?.payload?.config?.reset;
            const newData = action.payload.data || [];

            if (isReset) {
                state.executionsData = newData;
            } else {
                state.executionsData = R.uniqBy((i: Execution) => i.id, R.concat(state.executionsData, newData));
            }
        },
        getStateMachineHistorySuccess(state, action) {
            if (Array.isArray(action.payload)) {
                state.executionHistoryData = action.payload;
                state.definition = null;
                // Infer status for old API
                const lastRow = action.payload[action.payload.length - 1];
                let inferredStatus = 'RUNNING';
                if (lastRow) {
                    const isError =
                        lastRow.type === 'Error' ||
                        lastRow.type === 'Exception' ||
                        lastRow.events?.find((e: any) => e.type.includes('Fail'));

                    // Check for global terminal events in the history if available
                    const globalSucceeded = action.payload.find((row: any) => row.name === 'ExecutionSucceeded');
                    const globalFailed = action.payload.find(
                        (row: any) => row.name === 'ExecutionFailed' || row.name === 'Error' || row.name === 'Exception'
                    );

                    if (isError || globalFailed) {
                        inferredStatus = 'FAILED';
                    } else if (lastRow.events?.length >= 2 || globalSucceeded) {
                        inferredStatus = 'SUCCEEDED';
                    }
                }
                state.executionDetail = { status: inferredStatus } as any;
            } else {
                state.executionHistoryData = action.payload.table;
                state.executionDetail = action.payload.execution;
                state.definition = action.payload.definition;
            }
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        clearState(state, action) {
            state.executionsData = [];
        },
        clearExecutionHistoryState(state) {
            state.executionHistoryData = [];
            state.executionDetail = null;
            state.definition = null;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

const ENV = process.env;

export function getStateMachinesList() {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));

            const response = await axios('/stateMachines', {
                baseURL: ENV.REACT_APP_SERVICES_URL,
                method: 'GET'
            });

            if (response.status !== 200) throw new Error(response.data);

            dispatch(slice.actions.getStateMachinesListSuccess(response?.data));
            dispatch(slice.actions.setLoading(false));
        } catch (error) {
            console.error(error);

            dispatch(slice.actions.hasError(error));
        }
    };
}

export function clearState(clear: []) {
    return async () => {
        try {
            dispatch(slice.actions.clearState(clear));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getExecutionsList({ stateMachineId, search, filter, startDate, endDate, nextToken, reset }: GetProps) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));

            const response = await axios(`/stateMachines/${stateMachineId}`, {
                params: {
                    ...(search && { search }),
                    ...(filter && { filter }),
                    ...(startDate && { startDate }),
                    ...(endDate && { endDate })
                },
                headers: {
                    ...(nextToken && { 'x-next-page-token': nextToken })
                },
                baseURL: ENV.REACT_APP_SERVICES_URL,
                method: 'GET'
            });

            if (response.status !== 200) throw new Error(response.data);

            // Pass reset flag to reducer via response config
            (response as any).config.reset = reset;

            dispatch(slice.actions.getExecutionsListSuccess(response));
            dispatch(slice.actions.setLoading(false));
        } catch (error) {
            console.error(error);

            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getExecutionHistory(stateMachineId: string, executionId: string, silent = false) {
    return async () => {
        try {
            if (!silent) {
                dispatch(slice.actions.clearExecutionHistoryState());
                dispatch(slice.actions.setLoading(true));
            }

            const response = await axios(`/stateMachines/${stateMachineId}/${executionId}`, {
                baseURL: ENV.REACT_APP_SERVICES_URL,
                method: 'GET'
            });

            if (response.status !== 200) throw new Error(response.data);

            dispatch(slice.actions.getStateMachineHistorySuccess(response?.data));
            if (!silent) dispatch(slice.actions.setLoading(false));
        } catch (error) {
            console.error(error);

            dispatch(slice.actions.hasError(error));
        }
    };
}

export function reprocess(stateMachineId: string, executionId: string) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));

            const response = await axios(`/stateMachines/${stateMachineId}/${executionId}`, {
                baseURL: ENV.REACT_APP_SERVICES_URL,
                method: 'PUT'
            });

            if (response.status !== 200) throw new Error(response.data);

            dispatch(getExecutionsList({ stateMachineId, reset: true }));
            dispatch(slice.actions.setLoading(false));
        } catch (error) {
            console.error(error);

            dispatch(slice.actions.hasError(error));
        }
    };
}

export function redrive(stateMachineId: string, executionId: string) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));

            const response = await axios(`/stateMachines/${stateMachineId}/${executionId}/redrive`, {
                baseURL: ENV.REACT_APP_SERVICES_URL,
                method: 'POST'
            });

            if (response.status !== 200) throw new Error(response.data);

            dispatch(getExecutionHistory(stateMachineId, executionId));
            dispatch(slice.actions.setLoading(false));
        } catch (error) {
            console.error(error);

            dispatch(slice.actions.hasError(error));
        }
    };
}

export function bulkRedrive(stateMachineId: string, executionIds: string[]) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));

            const response = await axios(`/stateMachines/${stateMachineId}/bulk-redrive`, {
                baseURL: ENV.REACT_APP_SERVICES_URL,
                method: 'POST',
                data: { executionIds }
            });

            if (response.status !== 200) throw new Error(response.data);

            dispatch(getExecutionsList({ stateMachineId, reset: true }));
            dispatch(slice.actions.setLoading(false));
            return response.data;
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
            dispatch(slice.actions.setLoading(false));
            throw error;
        }
    };
}
